import type {
  ContentLibraryConfig,
  ContentLibraryMapping,
  FormDSLContext,
  SelectOption,
} from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createFormDataDependencyKey,
  resolveInputs,
} from './contentLibraryUtils';

interface UseContentLibraryOptionsParams {
  config: ContentLibraryConfig | undefined;
  dslContext: FormDSLContext | undefined;
  formData: Record<string, unknown>;
  skip?: boolean;
}

interface UseContentLibraryOptionsResult {
  options: SelectOption[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

/**
 * Gets a nested value from an object using dot notation path.
 * e.g., getNestedValue({ metadata: { mandatory: true } }, 'metadata.mandatory') => true
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current: unknown, key) => {
    if (
      current &&
      typeof current === 'object' &&
      key in (current as Record<string, unknown>)
    ) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Maps a Content Library API response option to a SelectOption using configurable mapping.
 * Falls back to default field names if mapping is not specified.
 */
function mapOptionWithConfig(
  opt: Record<string, unknown>,
  mapping?: ContentLibraryMapping
): SelectOption & { disabled?: boolean; metadata?: unknown } {
  const valuePath = mapping?.value || 'value';
  const labelPath = mapping?.label || 'label';
  const descriptionPath = mapping?.description || 'description';
  const disabledPath = mapping?.disabled;

  const value = getNestedValue(opt, valuePath);
  const label = getNestedValue(opt, labelPath);
  const description = getNestedValue(opt, descriptionPath);
  const disabled = disabledPath ? getNestedValue(opt, disabledPath) : undefined;

  return {
    value: String(value ?? ''),
    label: String(label ?? ''),
    helpText: description ? String(description) : undefined,
    disabled: disabled === true,
    metadata: opt.metadata,
  };
}

/**
 * Hook to fetch options from Content Library with pagination support.
 * Automatically refetches when input dependencies change.
 */
export function useContentLibraryOptions({
  config,
  dslContext,
  formData,
  skip = false,
}: UseContentLibraryOptionsParams): UseContentLibraryOptionsResult {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const lastFetchKey = useRef<string | null>(null);
  const resolvedInputsRef = useRef<Record<string, unknown> | null>(null);

  // Create a stable key based only on form fields this config depends on
  const formDataDependencyKey = createFormDataDependencyKey(
    config?.inputs,
    formData
  );

  const fetchOptions = useCallback(
    async (loadMore = false) => {
      if (!config?.contentId || skip) {
        setOptions([]);
        setCursor(null);
        setHasMore(false);
        return;
      }

      const resolvedInputs = resolveInputs(config.inputs, dslContext, formData);

      if (resolvedInputs === null) {
        setOptions([]);
        setCursor(null);
        setHasMore(false);
        return;
      }

      const fetchKey = `${config.contentId}:${JSON.stringify(resolvedInputs)}`;

      // If inputs changed, reset pagination
      if (fetchKey !== lastFetchKey.current) {
        lastFetchKey.current = fetchKey;
        resolvedInputsRef.current = resolvedInputs;
        setCursor(null);
        setHasMore(false);
        if (loadMore) return; // Don't load more if inputs changed
      }

      // Prepare request body with cursor for pagination
      const requestBody: Record<string, unknown> = { ...resolvedInputs };
      if (loadMore && cursor) {
        requestBody.cursor = cursor;
      }

      if (loadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const response = await fetcher(`content-library/${config.contentId}`, {
          method: 'POST',
          body: requestBody,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              `Failed to fetch from content library: ${config.contentId}`
          );
        }

        const data = await response.json();

        const mappedOptions: SelectOption[] = (data.options || []).map(
          (opt: Record<string, unknown>) =>
            mapOptionWithConfig(opt, config.mapping)
        );

        if (loadMore) {
          // Append to existing options
          setOptions((prev) => [...prev, ...mappedOptions]);
        } else {
          // Replace options
          setOptions(mappedOptions);
        }

        // Update pagination state
        const newCursor = data.metadata?.cursor;
        const totalCount = data.metadata?.count;
        setCursor(newCursor || null);

        // Determine if there are more items to load
        if (loadMore) {
          const currentTotal = options.length + mappedOptions.length;
          setHasMore(
            !!newCursor &&
              (totalCount
                ? currentTotal < totalCount
                : mappedOptions.length > 0)
          );
        } else {
          setHasMore(
            !!newCursor &&
              (totalCount ? mappedOptions.length < totalCount : false)
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        if (!loadMore) {
          setOptions([]);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [config, dslContext, formData, skip, cursor, options.length]
  );

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && cursor) {
      fetchOptions(true);
    }
  }, [hasMore, isLoadingMore, cursor, fetchOptions]);

  const refetch = useCallback(() => {
    lastFetchKey.current = null;
    fetchOptions(false);
  }, [fetchOptions]);

  // Only re-fetch when relevant dependencies change (not entire formData)
  useEffect(() => {
    fetchOptions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config?.contentId,
    config?.inputs,
    dslContext,
    formDataDependencyKey,
    skip,
  ]);

  return {
    options,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
  };
}
